import type { FlowsType } from "@/types/entities/flows";
import { cn } from "@/utils/theme";
import { useEffect } from "react";
import {
  Badge,
  Icon,
  ScrollArea,
  Select,
  SelectContent,
  SelectTrigger,
} from "@elaniintc/goes-design-system";
import { SelectItem } from "@radix-ui/react-select";
import { useInView } from "react-intersection-observer";

interface FlowSelectorProps {
  flows: FlowsType[] | undefined;
  selectedFlow?: FlowsType | null;
  onFlowChange: (flow: FlowsType) => void;
  className?: string;
  placeholder?: string;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  isLoading?: boolean;
}

export const FlowSelector = ({
  onFlowChange,
  flows,
  selectedFlow,
  placeholder,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
  isLoading = false,
}: FlowSelectorProps) => {
  const { ref: sentinelRef, inView } = useInView({
    root: null,
    rootMargin: "0px",
    threshold: 0,
  });

  useEffect(() => {
    if (inView && hasMore && !isLoadingMore) {
      onLoadMore?.();
    }
  }, [inView, hasMore, isLoadingMore, onLoadMore]);

  return (
    <Select
      onValueChange={(val) => {
        const flow = flows?.find((f) => f.id === val);
        if (flow) {
          onFlowChange(flow);
        }
      }}
      value={selectedFlow?.id || ""}
    >
      <SelectTrigger className="min-w-0 w-full flex rounded-lg" group="left">
        <div className="w-full flex gap-3 items-center">
          {selectedFlow ? (
            <div className="w-full flex items-center gap-3">
              <Icon type="far" name="diagram-project" className="text-info" />
              <div className="flex gap-2 items-center flex-auto">
                <span className="text-body">{selectedFlow.name}</span>
              </div>
              <Badge shape="pill" size="sm" variant="neutral">
                v {selectedFlow.version ?? "1.0.0"}
              </Badge>
            </div>
          ) : (
            <p className="truncate text-body-quiet">{placeholder}</p>
          )}
        </div>
      </SelectTrigger>
      <SelectContent
        align="start"
        side="bottom"
        className="overflow-y-auto border border-neutral py-2"
      >
        <ScrollArea className="max-h-[264px]" role="listbox" aria-label="Flows">
          {isLoading && !flows?.length ? (
            <div className="flex justify-center items-center py-4 px-3">
              <Icon
                type="far"
                name="spinner-third"
                className="animate-spin text-xl text-body"
              />
            </div>
          ) : flows && flows.length > 0 ? (
            flows.map((flow) => (
              <SelectItem
                key={`${flow.id}-${flow.name}`}
                value={flow.id || ""}
                asChild
                className={cn(
                  "cursor-pointer",
                  "w-full",
                  "rounded-lg px-3 py-2 text-body data-[state=checked]:bg-surface-action-accent data-[state=checked]:text-body-loud hover:text-body-loud hover:bg-neutral-object-accent-quiet outline-none",
                )}
              >
                <div className="w-full flex items-center gap-3 flex-auto">
                  <Icon
                    type="far"
                    name="diagram-project"
                    className="text-info"
                  />
                  <div className="flex gap-2 items-center flex-auto">
                    <span className="text-body">{flow.name}</span>
                  </div>
                  <Badge shape="pill" size="sm" variant="neutral">
                    v {flow.version ?? "1.0.0"}
                  </Badge>
                </div>
              </SelectItem>
            ))
          ) : (
            <div className="px-3 py-4 text-center text-body-quiet">
              No hay flujos disponibles
            </div>
          )}
          {hasMore && (
            <div
              ref={sentinelRef}
              className="px-3 py-2 text-body-quiet select-none"
            >
              {isLoadingMore && (
                <div className="flex justify-center items-center py-0.5 px-3 gap-4 ">
                  <Icon
                    type="far"
                    name="spinner-third"
                    className="animate-spin text-xl text-body"
                  />
                </div>
              )}
            </div>
          )}
        </ScrollArea>
      </SelectContent>
    </Select>
  );
};
